import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { right } from '@popperjs/core';
import {
  AccessPermissionService,
  DELETE_RECORD_LABEL,
  StorageTypeEnum,
  dblclickTooltipDef,
  defaultColumn,
  grid_widths_map,
} from '@piqet-angular/shared';
import { ProjectsFacade } from '@piqet-angular/data';
import { cloneDeep } from 'lodash';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { Subject } from 'rxjs';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class ProjectScenarioConsumerDataGridService {
  editConsumerSuccess = new Subject<any>();
  editConsumerSuccessData$ = this.editConsumerSuccess.asObservable();
  deleteConsumer = new Subject<any>();
  deleteConsumerData$ = this.deleteConsumer.asObservable();
  event: any;
  inlineEditing = new Subject<any>();
  inlineEditingObservable$ = this.inlineEditing.asObservable();
  constructor(
    private projectFacade: ProjectsFacade,
    private swalService: SwalService,
    private toaster: ToastrService,
    private accessService: AccessPermissionService
  ) {}
  projectScenarioConsumerPayload: any;
  StorageTypeEnum = StorageTypeEnum;

  accessPermission(): boolean {
    return this.accessService.hasPermission('edit', '', 'packaging');
  }

  projectScenarioConsumerData(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'StorageProcessname',
      headerName: 'Storage process',
      field: 'storageProcessname',
      cellStyle: { 'text-decoration': 'underline' },
      // sort: 'desc',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      wrapHeaderText: true,
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          this.projectFacade.openNewAddProjectRetail(
            params.data.scenarioId,
            params.data.id,
            'Review consumer',
            this.StorageTypeEnum.Consumer,
            'consumer-list'
          );
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'StorageDays',
      headerName: 'Storage days',
      field: 'storageDays',
      // sort: 'desc',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      wrapHeaderText: true,
      resizable: false,
      editable: this.accessPermission(),
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'StorageDays') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellRenderer: (params: any) => {
        return (
          '<span class="editable-icon project-inline-setWidth">' +
          params.value +
          '<i class="fa fa-pencil icon-opacity" aria-hidden="true"></i></span>'
        );
      },
      cellEditorParams: {
        from: 'project-pages-inline',
      },
      cellClass: ['outerGridEdit', 'outerGridProjectEdit', 'elipsisFields'],
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = params.data[params.colDef.field] !== newVal;
        const oldValue = params.data[params.colDef.field];
        params = cloneDeep(params);
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        const field = params.colDef.field;
        const nodeId = params.data.id;
        const inlineObject = {
          entityId: 'id',
          entityIdValue: params.data['id'],
          entityColumnName: params.colDef.field,
          filter: 'agTextColumnFilter',
          entityColumnValue: params.data[params.colDef.field],
        };
        if (!this.isEmpty(newVal) && valueChanged) {
          this.inlineEditing.next(inlineObject);
          return valueChanged;
        } else {
          this.toaster.error('Storage days is required');
          return oldValue;
        }
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'PercentageProductLoss',
      headerName: 'Product loss (%)',
      field: 'percentageProductLoss',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      wrapHeaderText: true,
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ProductLossName',
      headerName: 'Destination of product loss',
      field: 'productLossName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      wrapHeaderText: true,
      resizable: false,
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transport to consumer',
      field: 'transport',
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'iconsOfTransport',
      wrapText: true,
      autoHeight: true,
      wrapHeaderText: true,
      lockVisible: false,
      resizable: false,
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'RegionName',
      headerName: 'Destination',
      field: 'regionName',
      wrapHeaderText: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
      },
    });

    this.accessPermission() &&
      colDef.push({
        ...defaultColumn,
        colId: 'Actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        cellClass: 'my-permission-cursor-pointer',
        cellRenderer: 'actionRenderer',
        cellRendererParams: {
          showEdit: true,
          showDelete: true,
          value: 'manageTeams',
          deleteData: (data: any) => {
            this.deleteConfirmation(data);
          },
          editData: (data: any, pathName: any) => {
            this.projectFacade.openNewAddProjectRetail(
              data.scenarioId,
              data.id,
              'Review consumer',
              this.StorageTypeEnum.Consumer,
              pathName
            );
          },
        },
        lockVisible: false,
        lockPosition: right,
        resizable: false,
      });

    return colDef;
  }

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.projectScenarioConsumerData()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.projectScenarioConsumerPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'storageProcessname',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Desc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }

  deleteConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteConsumer.next([data]);
      }
    });
  }

  getConsumerDataList() {
    this.editConsumerSuccess.next(() => {});
  }
  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = val.trim();
    }
    return val === undefined || val === null || val === '';
  }
}
